/**
 * Admin V3 — Overview module — "Demande en temps réel" card (Row 2 right)
 *
 * Shows current demand level (gradient pill), surge multiplier, pending
 * requests, available drivers, demand ratio, optional assigned rides,
 * and update timestamp.
 *
 * getDemandTone() is a verbatim port from AdminDashboard.js:91-96 — same
 * gradient palette + same border + same dot colour per level.
 *
 * Data source: GET /demand/stats
 *   - demand_level, surge_multiplier, pending_requests, available_drivers,
 *     demand_ratio, assigned_rides (optional), timestamp
 *
 * Explicit error state per owner directive (Phase 2.1 decision #5).
 * Timestamp displayed per Phase 2.1 decision #3.
 */

import React from 'react';
import { Activity, AlertCircle, Clock } from 'lucide-react';
import { SectionCard, EmptyState, cn } from '../../shared/visualTokens';
import { t } from '../../i18n';

// Verbatim from AdminDashboard.js:91-96 — same gradient palette per demand level.
// labelKey resolves via i18n; legacy hard-coded the French labels inline.
const getDemandTone = (level) => {
  if (level === 'very_high') {
    return { card: 'from-rose-50 to-orange-50 border-rose-200', text: 'text-rose-700', dot: 'bg-rose-500', labelKey: 'overview.demand.level.very_high' };
  }
  if (level === 'high') {
    return { card: 'from-orange-50 to-amber-50 border-orange-200', text: 'text-orange-700', dot: 'bg-orange-500', labelKey: 'overview.demand.level.high' };
  }
  if (level === 'normal') {
    return { card: 'from-blue-50 to-cyan-50 border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500', labelKey: 'overview.demand.level.normal' };
  }
  return { card: 'from-emerald-50 to-green-50 border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500', labelKey: 'overview.demand.level.low' };
};

const formatTs = (iso, locale) => {
  if (!iso) return '';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'fr-FR', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).format(d);
  } catch {
    return String(iso);
  }
};

const StatLine = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-semibold text-slate-900">{value ?? '—'}</span>
  </div>
);

const OverviewDemandCard = ({ demand, locale = 'fr' }) => {
  const { data, loading, error } = demand;

  if (loading) {
    return (
      <SectionCard
        title={t('overview.demand.title', locale)}
        icon={Activity}
        iconClass="bg-slate-100 text-slate-600"
      >
        <EmptyState icon={Clock} title={t('overview.empty', locale)} />
      </SectionCard>
    );
  }

  if (error || !data) {
    return (
      <SectionCard
        title={t('overview.demand.title', locale)}
        icon={Activity}
        iconClass="bg-slate-100 text-slate-600"
      >
        <EmptyState
          icon={AlertCircle}
          title={t('overview.error', locale)}
          desc={error || undefined}
        />
      </SectionCard>
    );
  }

  const tone = getDemandTone(data.demand_level);
  const surge = data.surge_multiplier != null ? `x${Number(data.surge_multiplier).toFixed(2)}` : '—';
  const ts = formatTs(data.timestamp, locale);

  return (
    <SectionCard
      title={t('overview.demand.title', locale)}
      icon={Activity}
      iconClass="bg-slate-100 text-slate-600"
    >
      <div className="space-y-4">
        {/* Gradient pill showing demand level */}
        <div className={cn('rounded-xl border bg-gradient-to-br p-4', tone.card)}>
          <div className="flex items-center gap-3">
            <div className={cn('h-3 w-3 rounded-full', tone.dot)} />
            <span className={cn('text-sm font-semibold', tone.text)}>
              {t(tone.labelKey, locale)}
            </span>
            <span className={cn('ml-auto rounded-full bg-white/60 px-2.5 py-0.5 text-xs font-bold', tone.text)}>
              {surge}
            </span>
          </div>
        </div>

        {/* Stat lines */}
        <div className="space-y-2.5">
          <StatLine label={t('overview.demand.pending', locale)}   value={data.pending_requests} />
          <StatLine label={t('overview.demand.available', locale)} value={data.available_drivers} />
          <StatLine
            label={t('overview.demand.ratio', locale)}
            value={data.demand_ratio != null ? Number(data.demand_ratio).toFixed(2) : null}
          />
          {data.assigned_rides != null && (
            <StatLine label={t('overview.demand.assigned', locale)} value={data.assigned_rides} />
          )}
        </div>

        {/* Timestamp */}
        {ts && (
          <div className="text-right text-[11px] text-slate-400 pt-1">
            {t('overview.demand.updated', locale)} {ts}
          </div>
        )}
      </div>
    </SectionCard>
  );
};

export default OverviewDemandCard;
